import { isObject } from 'util';

import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, IeAnexo, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { ActionAPI, AutocompleteAPI, DateFieldAPI, FetchAPI, FieldAPI, FileUploaderAPI, FormAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnChange, OnClick, OnClose, OnEdit, OnMount, OnSelect } from '@philips-emr/tws-core/lib/client';
import { AbortableSubmitEvent, FetchEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HistoryService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isContentfulString, isEmptyObject } from '@philips-emr/tws-utils';

import { MigracaoContratoAliases as Aliases, FragmentNames } from '../codegen';
import { Anexo } from '../interfaces/anexo';

import BaseController from './BaseController';

interface Retorno {
  nrSeqSolicAlt: number;
  nrSeqAnexo: number;
  filepath: string;
  dtMigracao: string;
  filename: string;
  nrSeqContrato: number;
}

@Controller({ fragment: FragmentNames.MIGRACAO_CONTRATO, component: Aliases.PANEL_MIGRACAO_CONTRATO })
class MigracaoContratoController extends BaseController {

  private regraAnexos: Anexo[];

  @Inject(Aliases.FILE_UPLOADER)
  private fileUploaderAPI: FileUploaderAPI;
  @Inject(Aliases.FORM_DADOS)
  private formDados: FormAPI;
  @Inject(Aliases.FIELD_PAGADOR)
  private fieldPagador: FieldAPI;
  @Inject(Aliases.FIELD_PRODUTO)
  private fieldProduto: FieldAPI;
  @Inject(Aliases.FIELD_TABELA_PRECO)
  private fieldTabelaPreco: FieldAPI;
  @Inject(Aliases.FETCH_LISTA_ANEXOS)
  private fetchAnexos: FetchAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastForm: ToastNotificationAPI;
  @Inject(Aliases.FIELD_DATA_LIMITE)
  private fieldDataLimite: FieldAPI;
  @Inject(Aliases.FIELD_FILE_UPLOADER)
  private fieldUpload: FieldAPI;
  @Inject(Aliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;
  @Inject(Aliases.FETCH_TABELA_PRECO)
  private fetchTabelaPreco: FetchAPI;
  @Inject(Aliases.FETCH_PRODUTO)
  private fetchProduto: FetchAPI;
  @Inject(Aliases.FETCH_PAGADOR)
  private fetchPagador: FetchAPI;
  @Inject(Aliases.FIELD_NOVO_CONTRATO)
  private fieldNovoContrato: FieldAPI;
  @Inject(Aliases.FETCH_FORM)
  private fetchForm: FetchAPI;
  @Inject(Aliases.ACTION_CANCELAR)
  private actionCancelar: ActionAPI;

  private retorno: boolean;

  @OnMount(Aliases.PANEL_MIGRACAO_CONTRATO)
  onMount(): void {
    const { nrSeqBeneficiario, nrSeqContrato } = HistoryService.getCurrentRouteParams();
    this.context.set({ nrSeqBeneficiario, nrSeqContrato });
    this.getPredefinedFiles();
    this.actionCancelar.setEnabled(false);
  }

  @OnAfterFetch(Aliases.FETCH_FORM)
  onAfterFetchForm(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const { nrSeqSolicAlt } = event.data as Retorno;
    this.actionCancelar.setEnabled(true);
    if (nrSeqSolicAlt) {
      this.retorno = true;
      this.formDados.setEditing(false);
      this.getListaAnexos();
    }
  }

  @OnEdit(Aliases.FORM_DADOS)
  onEdit(): void {
    this.getPredefinedFiles();
    this.getListaAnexos();
    this.retorno = true;
  }

  @OnCancel(Aliases.FORM_DADOS)
  oncancel(): void {

    if (!this.retorno) {
      const { nrSeqGrupoContratoWeb, nrSeqContrato } = HistoryService.getCurrentRouteParams();
      HistoryService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO, { nrSeqGrupoContratoWeb, nrSeqContrato });
      return;
    }
    this.getListaAnexos();
    this.fetchForm.forceFetch();
    this.retorno = false;
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Record<string, string>>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value as unknown as Retorno;

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.MIGRACAO_BENEFIACIARIO;

    FileService.download(Endpoint.GET.MIGRAÇÃO_ANEXO_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnChange(Aliases.AUTOCOMPLETE_NOVO_CONTRATO)
  onChangePagador(event: AutocompleteAPI): void {
    const { nrSeqContrato: nrSeqContatoSelecionado } = event.value;

    this.fieldPagador.setData({});
    this.fieldProduto.setData({});
    this.fieldTabelaPreco.setData({});

    this.fetchProduto.setParameters({ nrSeqContatoSelecionado });
    this.fetchProduto.forceFetch();

    this.fetchPagador.setParameters({ nrSeqContatoSelecionado });
    this.fetchPagador.forceFetch();

    this.formDados.setData({
      ...this.formDados.getRecord(),
      contrato: event.value,
      pagador: {},
      plano: {},
      tabelaPreco: {},
    });

  }

  @OnChange(Aliases.AUTOCOMPLETE_PRODUTO)
  onChangeProduto(event: AutocompleteAPI): void {
    const { nrSeqPlano } = event.value;
    const { contrato } = this.fieldNovoContrato.getData();
    const { nrSeqContrato } = contrato as Retorno;

    this.formDados.setData({
      ...this.formDados.getRecord(),
      plano: event.value,
      tabelaPreco: {},
    });

    this.fetchTabelaPreco.setParameters({ nrSeqPlano, nrSeqContratoSelecionado: nrSeqContrato });
    this.fetchTabelaPreco.forceFetch();
  }

  @OnChange(Aliases.DATE_FIELD_MIGRACAO)
  onChangeDataMigracao(event: DateFieldAPI): void {
    if (!event.value) {
      return;
    }
    const parts = event.value.split('-');
    const dataLimite = new Date(parts[0], parts[1] - 1, parts[2]);
    dataLimite.setDate(dataLimite.getDate() - 1);
    this.fieldDataLimite.setValue(dataLimite.toISOString().slice(0, 10));
  }

  @OnMount(Aliases.FILE_UPLOADER)
  onMountAnexos(): void {
    this.getPredefinedFiles();
  }

  @OnBeforeSubmit(Aliases.FORM_DADOS)
  onSubmit(event: AbortableSubmitEvent): void {
    const dadosContrato = this.formDados.getRecord();
    const { nrSeqContrato: nrSeqContratoAtual, nrSeqBeneficiario: nrSeqSegurado, nrSeqGrupoContratoWeb } = HistoryService.getCurrentRouteParams();

    dadosContrato.anexos = this.handleAnexos(dadosContrato.anexos as Anexo[]);

    const body = { ...dadosContrato, nrSeqContratoAtual, nrSeqSegurado };
    this.panelLoader.setVisible(true);
    HttpService.post(Endpoint.POST.MIGRAR_CONTRATO, { nrSeqContrato: nrSeqContratoAtual }, { body })
      .then(() => {
        this.panelLoader.setVisible(false);
        this.actionCancelar.setEnabled(true);
        ToastServiceStipulator.success(MessageService.translate('stipulator.aviso-solicitacao-realizada'));
        this.historyService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO, { nrSeqGrupoContratoWeb, nrSeqContrato: nrSeqContratoAtual });
      })
      .catch(response => {
        this.panelLoader.setVisible(false);
        this.formDados.setEditing(true);
        const { body } = response;
        const openToast = (msg: string): void => {
          this.toastForm.setMessage(msg);
          this.toastForm.setVisible(true);
        };

        if (Array.isArray(body)) {
          const data = body as Anexo[];
          const message = `${MessageService.translate('stipulator.obrigatorio-anexar-arquivo')} ${data.map(anexo => anexo.dsLabelCampo).join(', ')}.`;
          return openToast(message);
        }

        if (isObject(body) && !isEmptyObject(body)) {
          return openToast(body[Object.keys(body)[0]]);
        }

        if (isContentfulString(body)) {
          return openToast(body);
        }
        event.abort();
      });
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.toastForm.setVisible(false);
  }

  @OnAfterFetch(Aliases.FETCH_LISTA_ANEXOS)
  onAfterFetchAnexos(event: FetchEvent) : void {
    this.fieldAnexos.setVisible(!isEmptyObject(event.data));
  }

  @OnClick(Aliases.ACTION_CANCELAR)
  onClick(): void {
    const record = this.formDados.getRecord();
    const nrSeqMigracao = record.nrSeqSolicAlt;
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.panelLoader.setVisible(true);
    void HttpService.post(Endpoint.POST.CANCELAR_MIGRACAO, { nrSeqContrato, nrSeqMigracao }).then(() => {
      this.panelLoader.setVisible(false);
      ToastServiceStipulator.success(MessageService.translate('stipulator.aviso-solicitacao-realizada'));
      this.historyService.push(APPLICATION_ROUTES.ALTERACAO_BENEFICIARIO, { nrSeqGrupoContratoWeb, nrSeqContrato });
    })
      .catch(() => {
        this.panelLoader.setVisible(false);
      });
  }

  private getListaAnexos(): void {
    const { nrSeqBeneficiario, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Record<string, Value>>(Endpoint.GET.MIGRAR_CONTRATO_ABERTA, { nrSeqBeneficiario, nrSeqContrato }).then(response => {
      const { body: solicAberta } = response;
      if (solicAberta) {
        const { nrSeqSolicAlt } = solicAberta;
        this.fetchAnexos.setParameters({ 'nrSeqMigracao': nrSeqSolicAlt });
        this.fetchAnexos.forceFetch();
        return;
      }
    });
  }

  private getPredefinedFiles(): void {
    void HttpService.get(Endpoint.GET.CHECK_TIPO_ANEXOS, { ieFuncionalidade: 14 })
      .then(response => {
        const anexos = response.body as Anexo[];
        const predefinedFiles = anexos.map(regras => {
          const { dsLabelCampo: value, nrSeqRegraAnexo: key } = regras;
          return { key: String(key), value };
        });
        this.regraAnexos = anexos;
        catchRenderException(() => this.fileUploaderAPI.setPredefinedFiles(predefinedFiles));
        if (predefinedFiles.length == 0) {
          this.fieldUpload.setVisible(false);
        }
      });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}
export default MigracaoContratoController;
